import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, isRef as _isRef } from "vue"

const _hoisted_1 = {
  key: 2,
  class: "text-end mb-3"
}

import { computed, ref, toRef, watch } from "vue";
import AddVatTuItemV2 from "@/components/add-vat-tu-items-v2/AddVatTuItemV2.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import { LOAI_DINH_MUC } from "@/core/constants/const";
import { AddVatTuItemsFor } from "@/core/enums/addVatTuItemsFor.enum";
import { swalErrNotification } from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import {
  IVatTuResponseBase,
  IVatTuXuatKhoResponseBase,
} from "@/core/interfaces/ApiResponses";
import { IVatTuSelectedInKho } from "@/core/interfaces/Models";
import ResponsiveLayout from "@/layout/_shared/ResponsiveLayout.vue";
import { getAddVatTuUseFor } from "./constants";
import { usePhieuXuatKhoSupplies } from "./composables/usePhieuXuatKhoSupplies";
import { useSuppliesQuantityValidation } from "./composables/useSuppliesQuantityValidation";
import PhieuXuatKhoVatTuTable from "./PhieuXuatKhoVatTuTable.vue";
import type {
  PhieuXuatKhoLoaiPhieu,
  PhieuXuatKhoVatTuTableMode,
} from "./types";


export default /*#__PURE__*/_defineComponent({
  __name: 'PhieuXuatKhoSuppliesSection',
  props: {
    loaiPhieu: {},
    readonly: { type: Boolean, default: false },
    idBienBan: {},
    loaiDinhMuc: {},
    idKho: {},
    addVatTuUseFor: {},
    showThuHoiColumns: { type: Boolean, default: true },
    showLyDoColumn: { type: Boolean, default: false },
    initialVatTuItems: {},
    initialKhoId: {},
    excludeIdPhieuXuat: {},
    isEditMode: { type: Boolean, default: false },
    hideKhoSelector: { type: Boolean, default: false }
  },
  emits: ["update:vat-tu-items", "update:vat-tu-selected-in-kho", "kho-change"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const { ButtonsType, ButtonTypeColors } = useButtonCustom();
const isAddVatTuModalOpen = ref(false);

const loaiPhieuRef = toRef(props, "loaiPhieu");
const idBienBanRef = toRef(props, "idBienBan");
const loaiDinhMucRef = toRef(props, "loaiDinhMuc");
const idKhoRef = toRef(props, "idKho");
const initialVatTuItemsRef = toRef(props, "initialVatTuItems");
const excludeIdPhieuXuatRef = toRef(props, "excludeIdPhieuXuat");
const isEditModeRef = toRef(props, "isEditMode");

const {
  addManualVatTuItems,
  buildVatTuSelectedInKhoPayload,
  fetchBbgtKhoList,
  isBbgtMode,
  isLoadingKho,
  isLoadingVatTu,
  isManualMode,
  khoOptions,
  onBbgtKhoChange,
  quantityRows,
  removeManualVatTuItem,
  selectingKhoId,
  selectingKhoItem,
  syncBbgtItemsFromQuantityRows,
  syncManualItemsFromQuantityRows,
  vatTuItems,
  vatTuSelectedInKho,
} = usePhieuXuatKhoSupplies({
  loaiPhieu: loaiPhieuRef,
  idBienBan: idBienBanRef,
  loaiDinhMuc: loaiDinhMucRef,
  idKho: idKhoRef,
  excludeIdPhieuXuat: excludeIdPhieuXuatRef,
  initialVatTuItems: initialVatTuItemsRef,
  isEditMode: isEditModeRef,
});

const { validateNgoaiDinhMucExportRows, validateThuHoiRows } =
  useSuppliesQuantityValidation(loaiDinhMucRef, (idVatTu) => {
    return (
      vatTuSelectedInKho.value.find(({ id_vat_tu }) => id_vat_tu === idVatTu)
        ?.con_thieu ?? 0
    );
  });

const addVatTuUseFor = computed(
  () => props.addVatTuUseFor ?? getAddVatTuUseFor(props.loaiPhieu)
);

const tableMode = computed((): PhieuXuatKhoVatTuTableMode => "pick");

const showPickFooter = computed(
  () =>
    (isBbgtMode.value && !!selectingKhoItem.value) ||
    (isManualMode.value && vatTuItems.value.length > 0)
);

const khoPlaceholderLabel = computed(() => {
  if (!isLoadingKho.value && !khoOptions.value.length) {
    return "Không có kho khả dụng";
  }

  return "Lựa chọn kho xuất vật tư";
});

const vatTuSelectedForModal = computed(() =>
  vatTuItems.value.map((item) => ({
    ...item,
    don_gia: item.pivot?.don_gia ? +item.pivot.don_gia : 0,
    so_luong: item.pivot?.so_luong ? +item.pivot.so_luong : 0,
  }))
);

const handleBbgtKhoChange = async (idKho: number) => {
  emit("kho-change", idKho);
  await onBbgtKhoChange(idKho);
};

const handleQuantityChange = () => {
  if (isBbgtMode.value) {
    syncBbgtItemsFromQuantityRows();
    emit("update:vat-tu-selected-in-kho", buildVatTuSelectedInKhoPayload());
  } else {
    syncManualItemsFromQuantityRows();
  }

  emit("update:vat-tu-items", [...vatTuItems.value]);
};

const onVatTuAdded = ({
  selectingVatTuItems,
}: {
  selectingVatTuItems: IVatTuResponseBase[];
}) => {
  isAddVatTuModalOpen.value = false;
  addManualVatTuItems(selectingVatTuItems);
  syncManualItemsFromQuantityRows();
  emit("update:vat-tu-items", [...vatTuItems.value]);
};

watch(
  () => [props.idBienBan, props.loaiDinhMuc, props.initialKhoId] as const,
  async ([idBienBan, loaiDinhMuc, initialKhoId]) => {
    if (!isBbgtMode.value || !idBienBan || !loaiDinhMuc) {
      return;
    }

    try {
      await fetchBbgtKhoList();

      const khoIdToSelect = Number(initialKhoId) || 0;

      if (khoIdToSelect > 0) {
        selectingKhoId.value = khoIdToSelect;
        await handleBbgtKhoChange(khoIdToSelect);
      }
    } catch (error) {
      await swalErrNotification(error, "Có lỗi khi lấy danh sách kho");
    }
  },
  { immediate: true }
);

const syncVatTuItemsFromQuantityRows = () => {
  if (isBbgtMode.value) {
    syncBbgtItemsFromQuantityRows();
  } else {
    syncManualItemsFromQuantityRows();
  }

  emit("update:vat-tu-items", [...vatTuItems.value]);
};

__expose({
  buildVatTuSelectedInKhoPayload,
  handleBbgtKhoChange,
  isLoadingKho,
  khoOptions,
  khoPlaceholderLabel,
  quantityRows,
  selectingKhoId,
  selectingKhoItem,
  syncVatTuItemsFromQuantityRows,
  vatTuItems,
  vatTuSelectedInKho,
  validateSupplies: async (): Promise<boolean | "missing_kho_or_qty"> => {
    if (isManualMode.value && !vatTuItems.value.length) {
      isAddVatTuModalOpen.value = true;

      return false;
    }

    // QuantityInputCustom can leave null/NaN while editing; normalize before checks.
    quantityRows.value.forEach((row) => {
      const qty = Number(row.so_luong_se_lay);

      if (row.so_luong_se_lay == null || Number.isNaN(qty)) {
        row.so_luong_se_lay = 0;
      } else {
        row.so_luong_se_lay = qty;
      }
    });

    if (isBbgtMode.value) {
      if (!selectingKhoItem.value) {
        return "missing_kho_or_qty";
      }

      const hasQuantity = quantityRows.value.some(
        ({ so_luong_se_lay }) => so_luong_se_lay > 0
      );

      if (!hasQuantity) {
        return "missing_kho_or_qty";
      }

      return (
        validateNgoaiDinhMucExportRows(quantityRows.value) &&
        validateThuHoiRows(quantityRows.value)
      );
    }

    return validateThuHoiRows(quantityRows.value);
  },
});

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_unref(isManualMode) && _ctx.idKho)
      ? (_openBlock(), _createBlock(AddVatTuItemV2, {
          key: 0,
          "is-modal-open": isAddVatTuModalOpen.value,
          "kho-id": _ctx.idKho,
          "use-for": addVatTuUseFor.value,
          "vat-tu-selected": vatTuSelectedForModal.value,
          onAdded: onVatTuAdded,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (isAddVatTuModalOpen.value = false))
        }, null, 8, ["is-modal-open", "kho-id", "use-for", "vat-tu-selected"]))
      : _createCommentVNode("", true),
    (_unref(isBbgtMode) && !_ctx.hideKhoSelector)
      ? (_openBlock(), _createBlock(ResponsiveLayout, { key: 1 }, {
          "col-1": _withCtx(() => [
            _createVNode(LabelCustom, { "is-required": true }, {
              default: _withCtx(() => [
                _createTextVNode("Kho")
              ]),
              _: 1
            })
          ]),
          "col-2": _withCtx(() => [
            _createVNode(_component_el_select, {
              modelValue: _unref(selectingKhoId),
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_isRef(selectingKhoId) ? (selectingKhoId).value = $event : null)),
              disabled: _ctx.readonly || _unref(isLoadingKho) || _ctx.isEditMode,
              loading: _unref(isLoadingKho),
              class: "w-full",
              name: "id_kho",
              onChange: handleBbgtKhoChange
            }, {
              default: _withCtx(() => [
                _createVNode(_component_el_option, {
                  disabled: true,
                  value: -1,
                  label: khoPlaceholderLabel.value
                }, null, 8, ["label"]),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(khoOptions), (kho) => {
                  return (_openBlock(), _createBlock(_component_el_option, {
                    key: kho.id,
                    label: kho.ten_kho,
                    value: kho.id
                  }, null, 8, ["label", "value"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue", "disabled", "loading"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (_unref(isManualMode) && !_ctx.readonly)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
          _createVNode(ButtonCustom, {
            "button-type": _unref(ButtonsType).ADD,
            "color-type": _unref(ButtonTypeColors).PRIMARY,
            onClick: _cache[2] || (_cache[2] = ($event: any) => (isAddVatTuModalOpen.value = true))
          }, {
            "button-label": _withCtx(() => [
              _createTextVNode("Vật tư")
            ]),
            _: 1
          }, 8, ["button-type", "color-type"])
        ]))
      : _createCommentVNode("", true),
    _createVNode(PhieuXuatKhoVatTuTable, {
      "is-edit-mode": _ctx.isEditMode,
      "is-loading": _unref(isLoadingVatTu),
      "loai-dinh-muc": _ctx.loaiDinhMuc,
      "loai-phieu": _ctx.loaiPhieu,
      mode: tableMode.value,
      "quantity-rows": _unref(quantityRows),
      readonly: _ctx.readonly,
      "selecting-kho-item": _unref(selectingKhoItem),
      "show-delete-action": _unref(isManualMode) && !_ctx.readonly,
      "show-ly-do-column": _ctx.showLyDoColumn,
      "show-pick-footer": showPickFooter.value,
      "show-thu-hoi-columns": _ctx.showThuHoiColumns,
      source: _unref(isBbgtMode) ? 'bbgt' : 'manual',
      supplies: _unref(vatTuItems),
      "vat-tu-selected-in-kho": _unref(vatTuSelectedInKho),
      onDeleteItem: _unref(removeManualVatTuItem),
      onQuantityChange: handleQuantityChange
    }, null, 8, ["is-edit-mode", "is-loading", "loai-dinh-muc", "loai-phieu", "mode", "quantity-rows", "readonly", "selecting-kho-item", "show-delete-action", "show-ly-do-column", "show-pick-footer", "show-thu-hoi-columns", "source", "supplies", "vat-tu-selected-in-kho", "onDeleteItem"])
  ], 64))
}
}

})